/**
 * iter-230bb — Session Expired Modal (replaces the inline yellow banner)
 * ======================================================================
 *
 * When a vendor's access+refresh tokens both expire mid-session, instead
 * of the previous full-width yellow banner (bad UX — covered page header,
 * crowded form fields), we now show a centered, non-dismissable modal
 * card. The page they were on stays mounted in the background (no flicker,
 * no losing form state); the modal demands re-auth before they can
 * interact again.
 *
 * Rendered globally by App.js so it appears regardless of which route
 * the vendor is on when the 401-cascade fires. Hidden for admins
 * (admin shell keeps the hard-redirect-to-/login UX since admin pages
 * are stateless enough to lose).
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { PasswordInput } from './ui/form-input';
import { AlertCircle, Lock, LogOut } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

const SessionExpiredBanner = () => {
  const { user, sessionExpired, login, clearSessionExpired, logout } = useAuth();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Only show for vendors with an expired session AND a known identity.
  // For admins we keep the hard-redirect path; if `user` is null we
  // have nothing to show "Welcome back, X" so fall back to /login.
  if (!sessionExpired || !user || user.role !== 'vendor') {
    return null;
  }

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const displayName = (user.business_name || user.name || user.email || '').split('@')[0];

  const handleReauth = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Please enter your password to continue');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      // iter-230bv-p4 — Hit /api/auth/login (the real login endpoint).
      // Previously this called /api/security/login which 404s — the
      // /security router only exposes refresh/logout/sudo/sessions,
      // there is no `/security/login`. Caused every re-auth attempt
      // to fall to "Login failed" even with the correct password.
      const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // Backend returns either a string `detail` ("email_not_found",
        // "Invalid credentials") OR a nested object for lockouts —
        // {detail: {code: "ACCOUNT_LOCKED", message: "..."}}. Flatten
        // safely so we never render [object Object].
        const detail =
          typeof body?.detail === 'string'
            ? body.detail
            : body?.detail?.message || body?.message || '';
        const friendly =
          detail === 'email_not_found'
            ? 'We could not find your account. Please log in again.'
            : detail === 'invalid_password' || detail === 'Invalid credentials'
            ? 'Incorrect password. Please try again.'
            : detail || `Login failed (HTTP ${res.status})`;
        throw new Error(friendly);
      }
      const data = await res.json();
      if (!data?.access_token || !data?.user) {
        throw new Error('Unexpected response shape — please try again.');
      }
      // `login()` clears sessionExpired and arms the storage guard.
      await login(data.user, data.access_token);
      setPassword('');
    } catch (err) {
      setError(err.message || 'Re-authentication failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      // Non-dismissable: cannot close via overlay click / escape / X.
      // The vendor MUST either re-authenticate or explicitly log out.
      onOpenChange={() => { /* no-op */ }}
    >
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        // Hide the default close (X) button — re-auth is mandatory.
        hideClose
        data-testid="session-expired-modal"
      >
        {/* Branded header */}
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <DialogHeader className="space-y-1 text-left">
              <DialogTitle className="text-base font-semibold text-white">
                Session Expired
              </DialogTitle>
              <DialogDescription className="text-xs text-amber-50/90">
                Please log in again to continue where you left off
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Re-auth form */}
        <form onSubmit={handleReauth} className="p-6 space-y-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
            <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
              Signed in as
            </p>
            <p className="text-sm font-medium text-gray-900 mt-0.5">
              {displayName}
            </p>
            <p className="text-xs text-gray-600">{user.email}</p>
          </div>

          <div className="space-y-1">
            <PasswordInput
              id="session-reauth-pwd"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoFocus
              disabled={submitting}
              data-testid="session-expired-password-input"
            />
          </div>

          {error && (
            <div
              className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2"
              data-testid="session-expired-error"
            >
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <Button
              type="submit"
              disabled={submitting || !password}
              data-testid="session-expired-submit-btn"
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
            >
              {submitting ? 'Signing in…' : 'Continue'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                clearSessionExpired();
                logout('/login');
              }}
              data-testid="session-expired-logout-btn"
              className="text-gray-700 hover:text-gray-900"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Log out
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SessionExpiredBanner;
